/**
 * Die Spalten des Monatsrasters (AP-22 Teil 5) — welche es gibt, in welcher Reihenfolge
 * sie ausgeliefert werden und wie eine gespeicherte Einstellung gelesen wird.
 *
 * Reihenfolge und Auswahl gehören dem Benutzer, mit Firmenstandard: Sie liegen in der
 * Ablage der Listen-Layouts (AP-23) unter `RASTER_LAYOUT_KEY`. Das Raster ist trotzdem
 * keine Liste nach dem Listen-Standard — es wird gepflegt, nicht gefiltert oder sortiert.
 *
 * **Ausblenden verbirgt nur.** Gerechnet wird immer mit allem, was in der Tageszeile
 * steht; eine ausgeblendete Eingabespalte behält ihre Werte.
 *
 * Eine neue Spalte kommt hier dazu (und bekommt ihre Zelle in `TagZeile`). Wer schon ein
 * eigenes Layout hat, sieht sie erst, wenn er sie einblendet — deshalb `optional` für
 * alles, was nicht jeder braucht.
 *
 * **Zulagen kommen aus dem Katalog der KV-Vorlage** (AP-22 Teil 6, K3), nicht aus dieser
 * Liste: Je eingeschalteter Zulage entstehen bis zu zwei optionale Spalten — die Eingabe
 * am Tag (`zin:<zulage>`) und ihr Ergebnis (`zul:<zulage>`). Ein anderer Kollektivvertrag
 * zeigt seine Zulagen, ohne dass hier etwas geändert wird.
 */
import { zulageSchluessel, type KvVorlage, type ZulageDef } from '@symphenia/payroll-core';

export const RASTER_LAYOUT_KEY = 'columnbar_layout_payroll_raster';

/** Feste Schlüssel dieser Datei, dazu `zin:<zulage>` und `zul:<zulage>` aus dem Katalog der Vorlage. */
export type RasterSpaltenKey = string;

export interface RasterSpalte {
  key: RasterSpaltenKey;
  /** Kurz, für den Tabellenkopf. */
  titel: string;
  /** Ausgeschrieben, für die Auswahl „Spalte einblenden". */
  name: string;
  hinweis?: string;
  links?: boolean;
  /** Berechnet, nicht erfasst — farblich abgesetzt. */
  rechnet?: boolean;
  /** Lässt sich nicht ausblenden: Ohne sie ist der Tag nicht erfassbar. */
  fest?: boolean;
  /** Im Auslieferungszustand nicht gezeigt. */
  optional?: boolean;
  /** Spalte einer Zulage des KV-Katalogs: die Eingabe am Tag oder ihr Ergebnis. */
  zulage?: { def: ZulageDef; art: 'eingabe' | 'ergebnis' };
}

/** Alle festen Spalten in der Reihenfolge des Auslieferungszustands; die optionalen am Ende. */
export const RASTER_SPALTEN: readonly RasterSpalte[] = [
  { key: 'kommt', titel: 'Kommt', name: 'Kommt', fest: true },
  { key: 'geht', titel: 'Geht', name: 'Geht', fest: true },
  {
    key: 'pause',
    titel: 'Pause',
    name: 'Pause',
    hinweis: 'Minuten. Greift die Pausenregel, wird die Standardpause eingetragen — 0 heißt keine Pause.',
  },
  { key: 'reise', titel: 'Reise', name: 'Reisezeit', hinweis: 'Reisezeit in Stunden' },
  { key: 'abfahrt', titel: 'Abf.', name: 'Abfahrt', hinweis: 'Abfahrt zur Montage' },
  { key: 'rueckfahrt', titel: 'Rückf.', name: 'Rückfahrt', hinweis: 'Rückfahrt von der Montage; leer = Geht' },
  { key: 'km', titel: 'km', name: 'Kilometer' },
  { key: 'lenker', titel: 'Lkr', name: 'Lenker', hinweis: 'Lenker — Wegzeit außerhalb der Betriebszeit wird Überstunde' },
  { key: 'naechtigung', titel: 'Näch', name: 'Nächtigung', hinweis: 'Nächtigung' },
  {
    key: 'termin',
    titel: 'Termin',
    name: 'Termin',
    hinweis: 'Persönlicher Termin während der Arbeitszeit, mit Dauer in Stunden',
    links: true,
  },
  { key: 'soll', titel: 'Soll', name: 'Soll', hinweis: 'Normalarbeitszeit laut Arbeitszeitmodell (Tages-Soll)', rechnet: true },
  {
    key: 'nlz',
    titel: 'NLZ',
    name: 'NLZ (Nichtleistungszeit)',
    hinweis:
      'Nichtleistungszeit: bezahlte Fehlzeiten und gesetzliche Feiertage. Aufklappen zeigt, woher sie kommt — dort erfasst du Urlaub, Krankenstand usw. in Stunden.',
    rechnet: true,
  },
  { key: 'naz', titel: 'NAZ', name: 'NAZ (Netto-Arbeitszeit)', hinweis: 'Netto-Arbeitszeit', rechnet: true },
  {
    key: 'mehr',
    titel: 'Mehr',
    name: 'Mehrarbeit',
    hinweis: 'Mehrarbeit (Teilzeit + 38,5–40 h) — je Kalenderwoche, auch über den Monatswechsel',
    rechnet: true,
  },
  { key: 'ues', titel: 'ÜS', name: 'Überstunden', hinweis: 'Überstunden roh', rechnet: true },
  {
    key: 'uesGew',
    titel: 'ÜS gew.',
    name: 'Überstunden gewichtet',
    hinweis: 'Überstunden gewichtet (× 167/143 × Satz)',
    rechnet: true,
  },
  { key: 'za', titel: 'ZA', name: 'Zeitausgleich', hinweis: 'Zeitausgleich-Bewegung des Tages', rechnet: true },
  { key: 'montage', titel: 'Mont.', name: 'Montagezeit', hinweis: 'Montagezeit', rechnet: true },
  { key: 'entfernung', titel: 'Entf.', name: 'Entfernungszulage', hinweis: 'Entfernungszulage (Betrag)', rechnet: true },
  {
    key: 'wegLenker',
    titel: 'Weg Lkr',
    name: 'Wegzeit außerhalb der Betriebszeit — als Lenker',
    hinweis: 'Wegzeit außerhalb der Betriebszeit an einem Tag als Lenker (Stunden)',
    rechnet: true,
    optional: true,
  },
  {
    key: 'wegBeifahrer',
    titel: 'Weg Beif.',
    name: 'Wegzeit außerhalb der Betriebszeit — als Beifahrer',
    hinweis: 'Wegzeit außerhalb der Betriebszeit an einem Tag als Beifahrer (Stunden)',
    rechnet: true,
    optional: true,
  },
  // AP-22 Teil 6: zwei Kennzeichen am Tag, die einen Satz ändern (Einstellungen → Überstunden bzw. Zulagen).
  {
    key: 'rueckruf',
    titel: 'Rückr.',
    name: 'Rückruf',
    hinweis: 'Rückruf zu nicht vorhersehbaren Überstunden — die Überstunden des Tages tragen den Rückruf-Satz',
    optional: true,
  },
  {
    key: 'verpflegung',
    titel: 'Verpfl.',
    name: 'Verpflegung gestellt',
    hinweis: 'Der Betrieb stellt an diesem Tag die Verpflegung — die große Entfernungszulage wird gekürzt',
    optional: true,
  },
];

const EINGABE = 'zin:';
const ERGEBNIS = 'zul:';

/**
 * Die Spalten der **eingeschalteten** Zulagen einer Vorlage, in der Reihenfolge ihres Katalogs.
 * `werte` sind die aufgelösten Einstellungen des Monats — eine ausgeschaltete Zulage bietet
 * ihre Spalten nicht an.
 */
export function zulagenSpalten(vorlage: KvVorlage, werte: Record<string, string | undefined>): RasterSpalte[] {
  const aus: RasterSpalte[] = [];
  for (const def of vorlage.zulagen) {
    if (werte[zulageSchluessel(def.schluessel).aktiv] !== '1') continue;
    const wo = `${def.name} (${def.fundstelle})`;
    if (def.bemessung === 'erfasste_stunden') {
      aus.push({
        key: `${EINGABE}${def.schluessel}`,
        titel: `${def.kurz} h`,
        name: `${def.name} — Stunden erfassen`,
        hinweis: `${wo}: Stunden des Tages. ${def.hinweis ?? ''}`.trim(),
        optional: true,
        zulage: { def, art: 'eingabe' },
      });
    } else if (def.bemessung === 'markierte_tage_naz') {
      aus.push({
        key: `${EINGABE}${def.schluessel}`,
        titel: def.kurz,
        name: `${def.name} — Tag anhaken`,
        hinweis: `${wo}: Tag anhaken. ${def.hinweis ?? ''}`.trim(),
        optional: true,
        zulage: { def, art: 'eingabe' },
      });
    }
    aus.push({
      key: `${ERGEBNIS}${def.schluessel}`,
      titel: def.prozent ? `${def.kurz} Std.` : `${def.kurz} €`,
      name: `${def.name} — ${def.prozent ? 'Stunden' : 'Betrag'}`,
      hinweis: `${wo}: ${def.prozent ? 'Stunden mit Zuschlag' : 'Betrag des Tages (Menge × Satz)'}. ${def.hinweis ?? ''}`.trim(),
      rechnet: true,
      optional: true,
      zulage: { def, art: 'ergebnis' },
    });
  }
  return aus;
}

/** Feste Spalten und — mit Vorlage — die ihrer eingeschalteten Zulagen. */
export function alleRasterSpalten(vorlage?: KvVorlage | null, werte?: Record<string, string | undefined> | null): readonly RasterSpalte[] {
  return vorlage ? [...RASTER_SPALTEN, ...zulagenSpalten(vorlage, werte ?? {})] : RASTER_SPALTEN;
}

const FEST = new Map(RASTER_SPALTEN.map((s) => [s.key, s]));
const istZulagenSchluessel = (k: string) => k.startsWith(EINGABE) || k.startsWith(ERGEBNIS);

/** Die gespeicherte Einstellung: die gezeigten Spalten in ihrer Reihenfolge. */
export interface RasterLayout {
  columnKeys: RasterSpaltenKey[];
}

export const RASTER_STANDARD: RasterLayout = {
  columnKeys: RASTER_SPALTEN.filter((s) => !s.optional).map((s) => s.key),
};

/**
 * Liest eine gespeicherte Einstellung. Unbekannte und doppelte Schlüssel fallen weg
 * (eine Spalte kann entfallen sein), feste Spalten kommen vorn dazu, wenn sie fehlen.
 * Ohne brauchbare Einstellung gilt der Auslieferungszustand.
 *
 * Schlüssel von Zulagen bleiben stehen, auch wenn die Vorlage noch nicht geladen ist oder
 * die Zulage gerade ausgeschaltet ist — gezeigt wird ohnehin nur, was `sichtbareSpalten`
 * im Katalog findet. Sonst vergäße das Layout sie bei jedem Laden.
 */
export function rasterLayoutAus(roh: unknown): RasterLayout {
  const liste = (roh as { columnKeys?: unknown } | null)?.columnKeys;
  if (!Array.isArray(liste)) return RASTER_STANDARD;
  const spalten = [...new Set(liste.filter((k): k is RasterSpaltenKey => typeof k === 'string' && (FEST.has(k) || istZulagenSchluessel(k))))];
  if (spalten.length === 0) return RASTER_STANDARD;
  const fehlendeFeste = RASTER_SPALTEN.filter((s) => s.fest && !spalten.includes(s.key)).map((s) => s.key);
  return { columnKeys: [...fehlendeFeste, ...spalten] };
}

export function sichtbareSpalten(layout: RasterLayout, alle: readonly RasterSpalte[] = RASTER_SPALTEN): RasterSpalte[] {
  const je = new Map(alle.map((s) => [s.key, s]));
  return layout.columnKeys.map((k) => je.get(k)).filter((s): s is RasterSpalte => s !== undefined);
}

export function versteckteSpalten(layout: RasterLayout, alle: readonly RasterSpalte[] = RASTER_SPALTEN): RasterSpalte[] {
  return alle.filter((s) => !layout.columnKeys.includes(s.key));
}

/** Setzt `quelle` an die Stelle von `ziel` — wie beim Ziehen eines Spaltenkopfs auf einen anderen. */
export function verschiebeSpalte(layout: RasterLayout, quelle: RasterSpaltenKey, ziel: RasterSpaltenKey): RasterLayout {
  const spalten = [...layout.columnKeys];
  const von = spalten.indexOf(quelle);
  const nach = spalten.indexOf(ziel);
  if (von < 0 || nach < 0 || von === nach) return layout;
  spalten.splice(von, 1);
  spalten.splice(nach, 0, quelle);
  return { columnKeys: spalten };
}

export function blendeAus(layout: RasterLayout, key: RasterSpaltenKey): RasterLayout {
  if (FEST.get(key)?.fest) return layout;
  return { columnKeys: layout.columnKeys.filter((k) => k !== key) };
}

export function blendeEin(layout: RasterLayout, key: RasterSpaltenKey): RasterLayout {
  return layout.columnKeys.includes(key) ? layout : { columnKeys: [...layout.columnKeys, key] };
}
